import { useFormik } from 'formik';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { DateTime } from 'luxon';
import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useUpdate } from 'react-use';
import { v4 as uuidv4 } from 'uuid';

import type { Account } from 'app-components/flux/accounts';
import { accountsSelectors } from 'app-components/flux/accounts';
import { dateSelectors } from 'app-components/flux/date';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import type { InvestmentSymbolLookupItem } from 'app-components/flux/investment-holdings-v2';
import { securitiesUtils } from 'app-components/flux/securities';
import {
  transactionsActions,
  transactionsSelectors,
  transactionsTypes,
  transactionsUtils,
} from 'app-components/flux/transactions';
import { useExcludeFrom } from 'app-components/hooks/investment-forms';
import { tracker } from 'app-components/utils/core';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import type { TextFieldProps } from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import AttachmentsField from 'components/AttachmentsField';
import Dump from 'components/Dump';
import InfoTooltipIcon from 'components/InfoTooltipIcon';
import InvestmentTransactionTypeField from 'components/InvestmentTransactionTypeField';
import AccountField from 'components/QuickenControls/AccountField';
import CategoryField from 'components/QuickenControls/CategoryField';
import QTip from 'components/QuickenControls/QTip';
import SDatePicker from 'components/SDatePicker';
import useTxnActions from 'data/transactionActions/useTxnActions';
import trackEventTTI from 'utils/trackEventTTI';

import CashFlowTransactionForm from './CashFlowTransactionForm';
import InvestmentTransactionAddSharesForm from './InvestmentTransactionAddSharesForm';
import InvestmentTransactionAmountForm from './InvestmentTransactionAmountForm';
import InvestmentTransactionBuyBondsForm from './InvestmentTransactionBuyBondsForm';
import InvestmentTransactionDividentForm from './InvestmentTransactionDividentForm';
import InvestmentTransactionReinvestForm from './InvestmentTransactionReinvestForm';
import InvestmentTransactionRemoveSharesForm from './InvestmentTransactionRemoveSharesForm';
import InvestmentTransactionSharesForm from './InvestmentTransactionSharesForm';
import InvestmentTransactionStockSplitForm from './InvestmentTransactionStockSplitForm';
import type {
  InvestmentTransactionFormik,
  InvestmentTransactionFormType,
  InvestmentTransactionFormValues,
} from './types';
import { toTransactionProps } from './types';

type InvestmentTransactionEditFormProps = Omit<React.FormHTMLAttributes<HTMLFormElement>, 'onSubmit'> & {
  transaction?: Partial<InvestmentTransactionFormValues>;
  formikRef?: React.Ref<InvestmentTransactionFormik>;
  onClose?: (event?: React.SyntheticEvent, reason?: string) => void;
  onDirty?: (dirty: boolean) => void;
  context?: string;
};

type DropzoneRef = {
  getRootProps?: () => React.HTMLAttributes<HTMLFormElement>;
};

type SDatePickerProps = {
  id?: string;
  label?: string;
  maxDate?: DateTime;
  value?: DateTime | null;
  onChange?: (date: DateTime | null, value: string) => void;
  textFieldProps?: TextFieldProps;
};

const TypedSDatePicker = SDatePicker as React.ComponentType<SDatePickerProps>;

const InvestmentTransactionEditForm = React.memo((props: InvestmentTransactionEditFormProps) => {
  const { transaction, onClose, onDirty, formikRef, context, ...otherProps } = props;

  const securitiesEnabledFf = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'securities'));
  const futureTxnsFf = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'futureTxns'));
  const currentDate = useAppSelector(dateSelectors.getCurrentDate);
  const [dateInfoText, setDateInfoText] = useState('');

  const showCheckNumberField = useAppSelector((state) =>
    accountsSelectors.shouldAccountDisplayCheckNumberField(state, transaction?.accountId),
  );

  const dispatch = useAppDispatch();

  const openAccounts = useAppSelector((state) =>
    accountsSelectors.getOpenOrSelectedInvestmentAccounts(state, transaction?.accountId ?? ''),
  );
  const appearsOnYourStatement = useMemo(
    () =>
      `Appears as "${transaction?.cpData?.payee}" on ${DateTime.fromISO(
        transaction?.cpData?.postedOn || transaction?.postedOn || '',
      ).toLocaleString(DateTime.DATE_MED)}.`,
    [transaction],
  );

  const { saveTransactions } = useTxnActions({ wrapperId: context });

  const submitTransaction = (
    securityObj: InvestmentSymbolLookupItem | undefined,
    values: InvestmentTransactionFormValues,
    existingSecurity?: boolean,
  ): void => {
    const investmentTxnType = values.investmentTxnType || (values.type === 'CASH_FLOW' ? 'VIRTUAL_CASH_FLOW' : '');
    const transactionValues = toTransactionProps(values);
    if (investmentTxnType === 'VIRTUAL_CASH_FLOW') {
      saveTransactions([
        transactionsTypes.mkCashFlowTransaction({
          clientId: uuidv4().toUpperCase(),
          ...transactionValues,
          type: 'CASH_FLOW',
        }),
      ]);
    } else {
      const payload = new transactionsTypes.InvestmentTransaction({
        clientId: uuidv4().toUpperCase(),
        ...transactionsUtils.normalizeInvestmentTransaction(transactionValues),
        localSecurityId: existingSecurity ? values?.localSecurityId : securityObj?.security?.id,
      });

      trackEventTTI(tracker.events.txnSave, transactionsSelectors.getTrackingProps(undefined, payload));

      if (values.id) {
        dispatch(transactionsActions.updateTransaction(payload, { undo: { userMessage: 'Transaction updated.' } }));
      } else {
        dispatch(transactionsActions.createTransaction(payload, { undo: { userMessage: 'Transaction created.' } }));
      }
    }

    onClose?.(undefined, 'submit');
  };

  const formik = useFormik<InvestmentTransactionFormValues>({
    initialValues: {
      amount: 0,
      postedOn: currentDate.toISODate() ?? '',
      type: 'INVESTMENT',
      ...transaction,
      fullSecurity: undefined,
    },
    onSubmit: (values: InvestmentTransactionFormValues) => {
      if (
        securitiesEnabledFf &&
        values?.fullSecurity &&
        !values.fullSecurity?.security?.id &&
        values.fullSecurity?.name
      ) {
        securitiesUtils.createNewSecurityAndSendPackage(
          values?.fullSecurity,
          dispatch,
          assert,
          submitTransaction,
          values,
        );
      } else {
        submitTransaction(undefined, values, true);
      }
    },
  });

  useEffect(() => {
    formik.registerField('postedOn', {
      validate: (value: string) => {
        const date = DateTime.fromISO(value);
        if (!date?.isValid) {
          return 'please use valid date';
        }

        const yearFromNow = DateTime.now().plus({ years: 1 });
        if (futureTxnsFf && date > yearFromNow) {
          return `date must be before ${yearFromNow.toFormat('MM/dd/yyyy')}`;
        }
        return undefined;
      },
    });
    formik.registerField('accountId', {
      validate: (value: string | null | undefined) => (value ? undefined : 'please select an account'),
    });
    if (formik.values.type === 'INVESTMENT') {
      formik.registerField('investmentTxnType', {
        validate: (value: InvestmentTransactionFormValues['investmentTxnType']) =>
          value && value !== transactionsTypes.InvestmentTxnType.UNKNOWN ? undefined : 'please select type',
      });
    }
    return () => {
      formik.unregisterField('postedOn');
      formik.unregisterField('accountId');
      formik.unregisterField('investmentTxnType');
    };
  }, [formik, futureTxnsFf]);

  useEffect(() => {
    onDirty?.(formik.dirty);
  }, [formik.dirty, onDirty]);

  useImperativeHandle(formikRef, () => formik, [formik]);

  const investmentTxnType: InvestmentTransactionFormType =
    formik.values.investmentTxnType || (formik.values.type === 'CASH_FLOW' ? 'VIRTUAL_CASH_FLOW' : '');

  const ignoreIsDisabled = useExcludeFrom(formik);

  const typeSpecificForm = useMemo(() => {
    let form: React.ReactNode;
    switch (investmentTxnType) {
      case transactionsTypes.InvestmentTxnType.BUY:
      case transactionsTypes.InvestmentTxnType.COVER_SHORT_SELL:
        form = <InvestmentTransactionSharesForm formik={formik} expectedSign={-1} />;
        break;
      case transactionsTypes.InvestmentTxnType.SELL:
      case transactionsTypes.InvestmentTxnType.SELL_BONDS:
      case transactionsTypes.InvestmentTxnType.SHORT_SELL:
        form = <InvestmentTransactionSharesForm formik={formik} expectedSign={+1} />;
        break;
      case transactionsTypes.InvestmentTxnType.STOCK_DIVIDEND:
        form = <InvestmentTransactionDividentForm formik={formik} />;
        break;
      case transactionsTypes.InvestmentTxnType.REINVEST_DIVIDEND:
      case transactionsTypes.InvestmentTxnType.REINVEST_INTEREST:
      case transactionsTypes.InvestmentTxnType.REINVEST_CAPITAL_GAIN_LONG:
      case transactionsTypes.InvestmentTxnType.REINVEST_CAPITAL_GAIN_SHORT:
        form = <InvestmentTransactionReinvestForm formik={formik} />;
        break;
      case transactionsTypes.InvestmentTxnType.INCOME_DIVIDEND:
      case transactionsTypes.InvestmentTxnType.INCOME_INTEREST:
      case transactionsTypes.InvestmentTxnType.CAPITAL_GAIN_LONG:
      case transactionsTypes.InvestmentTxnType.CAPITAL_GAIN_SHORT:
      case transactionsTypes.InvestmentTxnType.INCOME_MISCELLANEOUS:
      case transactionsTypes.InvestmentTxnType.EXPENSE_MARGIN_INTEREST:
      case transactionsTypes.InvestmentTxnType.EXPENSE_MISCELLANEOUS:
      case transactionsTypes.InvestmentTxnType.RETURN_OF_CAPITAL:
      case transactionsTypes.InvestmentTxnType.UNKNOWN:
        form = <InvestmentTransactionAmountForm formik={formik} />;
        break;
      case transactionsTypes.InvestmentTxnType.ADD_SHARES:
        form = <InvestmentTransactionAddSharesForm formik={formik} />;
        break;
      case transactionsTypes.InvestmentTxnType.REMOVE_SHARES:
        form = <InvestmentTransactionRemoveSharesForm formik={formik} />;
        break;
      case transactionsTypes.InvestmentTxnType.BUY_BONDS:
        form = <InvestmentTransactionBuyBondsForm formik={formik} />;
        break;
      case transactionsTypes.InvestmentTxnType.STOCK_SPLIT:
        form = <InvestmentTransactionStockSplitForm formik={formik} />;
        break;
      case 'VIRTUAL_CASH_FLOW':
        form = <CashFlowTransactionForm formik={formik} />;
        break;
      // TODO: what's the use case?
      // case 'VIRTUAL_TRANSFER_SHARES':
      //   break;
      default:
        assert(!investmentTxnType, `unexpected investmentTxnType = ${formik.values.investmentTxnType}`);
        form = <InvestmentTransactionAmountForm formik={formik} />;
    }
    return form;
  }, [formik, investmentTxnType]);

  const tooltipText = useMemo(() => {
    let text: string | undefined;
    switch (investmentTxnType) {
      case transactionsTypes.InvestmentTxnType.BUY:
        text =
          'Enter the total price, the number of shares, and commission, and Simplifi will calculate the price per share. This is typically the most accurate way to record Buy transactions.';
        break;
      case transactionsTypes.InvestmentTxnType.SELL:
        text =
          'Enter the total sale price, the number of shares, and commission, and Simplifi will calculate the price per share. This is typically the most accurate way to record Sell transactions.';
        break;
      case transactionsTypes.InvestmentTxnType.STOCK_DIVIDEND:
        text =
          'A dividend payment made in the form of additional shares rather than a cash payout. Enter the number of new shares issued per share. For example, enter “.05” if you received 0.05 shares per existing share. Your cash balance will not be impacted.';
        break;
      case transactionsTypes.InvestmentTxnType.REINVEST_DIVIDEND:
        text =
          'A cash distribution of a portion of earnings which is then reinvested in the same security. Enter the dividend amount, shares reinvested (which may be fractional), and the commission. Simplifi will calculate the price per share and total cost (cost basis). Your cash balance is not impacted.';
        break;
      case transactionsTypes.InvestmentTxnType.REINVEST_INTEREST:
        text =
          'An interest payment which is then reinvested in the same holding. Enter the interest amount, shares/units reinvested (which may be fractional), and the commission. Simplifi will calculate the price per share and total cost (cost basis). Your cash balance is not impacted.';
        break;
      case transactionsTypes.InvestmentTxnType.REINVEST_CAPITAL_GAIN_LONG:
        text =
          'A capital gain distribution which is then reinvested in the same holding. Enter the distribution amount, shares/units reinvested (which may be fractional), and the commission. Simplifi will calculate the price per share and total cost (cost basis). Your cash balance is not impacted.';
        break;
      case transactionsTypes.InvestmentTxnType.REINVEST_CAPITAL_GAIN_SHORT:
        text =
          'A capital gain distribution which is then reinvested in the same holding. Enter the distribution amount, shares/units reinvested (which may be fractional), and the commission. Simplifi will calculate the price per share and total cost (cost basis). Your cash balance is not impacted.';
        break;
      case transactionsTypes.InvestmentTxnType.INCOME_DIVIDEND:
        text =
          'A cash distribution of a portion of earnings to the shareholder. The dividend will be added to your cash balance.';
        break;
      case transactionsTypes.InvestmentTxnType.INCOME_INTEREST:
        text = 'A cash, interest distribution to the shareholder. The interest will be added to your cash balance.';
        break;
      case transactionsTypes.InvestmentTxnType.CAPITAL_GAIN_LONG:
        text = 'A cash, capital gain distribution to the shareholder. The interest will be added to your cash balance.';
        break;
      case transactionsTypes.InvestmentTxnType.CAPITAL_GAIN_SHORT:
        text = 'A cash, capital gain distribution to the shareholder. The interest will be added to your cash balance.';
        break;
      case transactionsTypes.InvestmentTxnType.INCOME_MISCELLANEOUS:
        text =
          'Income related to a holding that is not a Dividend, Interest payment, or Capital Gain distribution. The income will be added to your cash balance.';
        break;
      case transactionsTypes.InvestmentTxnType.ADD_SHARES:
        text =
          'This transaction type allows you to enter holdings without affecting your cash balance. For example, when entering gift or inheritance holdings, or when transferring shares from another brokerage account. Enter the total price, the number of shares, and commission, and Simplifi will calculate the price per share. This is typically the most accurate way to record Add Shares transactions.';
        break;
      case transactionsTypes.InvestmentTxnType.REMOVE_SHARES:
        text = 'Used to remove shares of this security from the account without affecting the cash balance.';
        break;
      case transactionsTypes.InvestmentTxnType.BUY_BONDS:
        text =
          "Enter the face value (par value) of the bonds(s) purchased, the price per $100 and the commission Simplifi will also represent the bond in terms of “shares” for easy cost calculations based on the quoted price.Enter accrued interest paid at time of purchase (if any). Accrued interest is not included in your cost basis. It's good to include the due date as part of the security name (the due date may be referred to as the maturity or call date in your statement). This date helps you to identify and track the bond.";
        break;
      case transactionsTypes.InvestmentTxnType.SELL_BONDS:
        text =
          'Enter the total price, the number of shares, and commission, and Simplifi will calculate the price per share. This is typically the most accurate way to record Sell transactions.';
        break;
      case transactionsTypes.InvestmentTxnType.EXPENSE_MARGIN_INTEREST:
        text = 'The interest expense for a security you bought on margin.';
        break;
      case transactionsTypes.InvestmentTxnType.EXPENSE_MISCELLANEOUS:
        text = "Miscellaneous expense such as a broker's fee or mutual fund management fee.";
        break;
      case transactionsTypes.InvestmentTxnType.RETURN_OF_CAPITAL:
        text =
          'A return from an investment that is not considered income. This is not considered an investment gain and reduces the cost basis in the associated security. Investors are not taxed on this return until it begins to exceed their original investment value. The amount will be added to your cash balance.';
        break;
      case transactionsTypes.InvestmentTxnType.SHORT_SELL:
        text =
          'Enter the total sale price, the number of shares, and commission, and Simplifi will calculate the price per share. This is typically the most accurate way to record Short Sell transactions.';
        break;
      case transactionsTypes.InvestmentTxnType.COVER_SHORT_SELL:
        text =
          'A buy order made on a security that closes out an existing short position. Enter the total price, the number of shares, and commission, and Simplifi will calculate the price per share. This is typically the most accurate way to record Buy to Cover transactions.';
        break;
      case transactionsTypes.InvestmentTxnType.STOCK_SPLIT:
        text =
          'A corporate action in which a company divides or multiplies its existing shares. The total dollar value of the shares remains the same compared to pre-split amounts, because the split did not add any real value.';
        break;
      case 'VIRTUAL_CASH_FLOW':
        text = 'Cash flow transaction';
        break;
      case transactionsTypes.InvestmentTxnType.UNKNOWN:
        text = 'Please select valid type';
        break;
      default:
        assert(!investmentTxnType, `unexpected investmentTxnType = ${investmentTxnType}`);
    }
    return text;
  }, [investmentTxnType]);

  const dropzoneRef = useRef<DropzoneRef>(null);
  const update = useUpdate();

  // eslint-disable-next-line
  const { onClick, onFocus, onBlur, onKeyDown, tabIndex, ...dropzoneRootProps } =
    dropzoneRef.current?.getRootProps?.() || {};
  return (
    <form onSubmit={formik.handleSubmit} {...dropzoneRootProps} {...otherProps}>
      <Box display='flex' gap={3} flexDirection='column' position='relative'>
        <Dump obj={{ formik, transaction }} />

        {transaction?.cpData?.payee && (
          <QTip wrapOnly>
            <Typography variant='captionRegular' color='textSecondary' noWrap display='block'>
              {appearsOnYourStatement}
            </Typography>
          </QTip>
        )}

        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <AccountField
            accounts={openAccounts}
            doNotIncludeClosed
            initialAccountId={formik.values.accountId}
            onSelected={(item: Account) => {
              formik.setFieldValue('accountId', item?.id);

              // If the account is set to be ignored, by default have it be ignored
              // If account is not set to be ignored, by default have it NOT be ignoreed unless it is disabled
              if (item.isExcludedFromBudgets) {
                formik.setFieldValue('isExcludedFromF2S', true);
              } else if (!ignoreIsDisabled) {
                formik.setFieldValue('isExcludedFromF2S', false);
              }
              if (item.isExcludedFromReports) {
                formik.setFieldValue('isExcludedFromReports', true);
              } else if (!ignoreIsDisabled) {
                formik.setFieldValue('isExcludedFromReports', false);
              }
            }}
            label='Account'
            readOnly={formik.values.source !== 'USER_ENTERED'}
            id='account-select'
            sx={{ width: 250 }}
          />

          <TypedSDatePicker
            id='posted-on'
            label='Date'
            maxDate={futureTxnsFf ? DateTime.now().plus({ year: 1 }) : undefined}
            value={formik.values.postedOn ? DateTime.fromISO(formik.values.postedOn) : null}
            onChange={async (date: DateTime | null, value: string) => {
              await formik.setFieldValue('postedOn', date?.isValid ? date?.toISODate?.() : value);
              await formik.setFieldTouched('postedOn', true);
              setDateInfoText(
                futureTxnsFf && date?.isValid && date > currentDate ? 'Future dates will create a Reminder.' : '',
              );
            }}
            textFieldProps={{
              error: Boolean((formik.touched.postedOn || formik.submitCount) && formik.errors.postedOn),
              helperText:
                ((formik.touched.postedOn || formik.submitCount) && (formik.errors.postedOn || dateInfoText)) || '',
            }}
          />
        </Box>

        <Box display='flex' alignItems='center' gap={2}>
          <InvestmentTransactionTypeField
            id='investmentTxnType'
            sx={{ minWidth: 250 }}
            value={investmentTxnType}
            onChange={(value: InvestmentTransactionFormType) => formik.setFieldValue('investmentTxnType', value)}
            textFieldProps={{
              error: Boolean(formik.submitCount && formik.errors.investmentTxnType),
              helperText: (formik.submitCount && formik.errors.investmentTxnType) || '',
            }}
          />

          <InfoTooltipIcon tooltipText={tooltipText} />

          {transactionsUtils.isCategoryVisibleOnInvestmentTxnType(investmentTxnType) && (
            <CategoryField
              id='category'
              editable
              disableUnderline
              textFieldVariant='filled'
              allowBlank
              createEnabled
              label='Category'
              width={240}
              value={formik.values.coa}
              onChange={(coa: InvestmentTransactionFormValues['coa']) => formik.setFieldValue('coa', coa)}
            />
          )}
        </Box>

        {typeSpecificForm}

        <Divider />

        <Box display='flex'>
          <Box>
            <Typography variant='captionRegular' color='text.secondary'>
              Exclusions
            </Typography>
            <Box>
              <FormControlLabel
                disabled={ignoreIsDisabled}
                checked={Boolean(formik.values.isExcludedFromF2S)}
                value={Boolean(formik.values.isExcludedFromF2S)}
                onChange={(_event: React.SyntheticEvent, newChecked: boolean) =>
                  formik.setFieldValue('isExcludedFromF2S', newChecked)
                }
                control={<Checkbox />}
                label='Exclude from Spending Plan'
                labelPlacement='end'
              />
              <FormControlLabel
                disabled={ignoreIsDisabled}
                checked={Boolean(formik.values.isExcludedFromReports)}
                value={Boolean(formik.values.isExcludedFromReports)}
                onChange={(_event: React.SyntheticEvent, newChecked: boolean) =>
                  formik.setFieldValue('isExcludedFromReports', newChecked)
                }
                control={<Checkbox />}
                label='Exclude from Reports'
                labelPlacement='end'
              />
            </Box>
          </Box>

          <Box display='flex' flexDirection='column' gap={1}>
            <Typography variant='captionRegular' color='text.secondary'>
              Additional info
            </Typography>
            <TextField
              id='memo'
              label='Note'
              sx={{ width: 280 }}
              value={formik.values.memo || ''}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                formik.setFieldValue('memo', event.target.value || '')
              }
            />

            <AttachmentsField
              id='attachments'
              attachments={formik.values.attachments}
              onChange={(_event: React.SyntheticEvent, attachments: InvestmentTransactionFormValues['attachments']) =>
                formik.setFieldValue('attachments', attachments)
              }
              showDropZone
              onDropzoneChanged={update}
              dropzoneRef={dropzoneRef}
            />

            {showCheckNumberField && transactionsUtils.isCheckNumberVisibleOnInvestmentTxnType(investmentTxnType) ? (
              <TextField
                id='checkNumber'
                label='Check #'
                value={formik.values.check?.number || ''}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  formik.setFieldValue('check.number', event.target.value || '')
                }
              />
            ) : null}
          </Box>
        </Box>
      </Box>
    </form>
  );
});

InvestmentTransactionEditForm.displayName = 'InvestmentTransactionEditForm';

export default InvestmentTransactionEditForm;
